<template>
  <div id="aiPopover">
    <div class="ai-content">
      <main class="main">
        <header class="main-header">
          <div class="left-side-wrapper">
            <div class="ai-close" @click="onClose()">
              <i class="fa-light fa-close"></i>
            </div>
            <h3>{{ $t("ai-sections.setup-section") }}</h3>
          </div>
        </header>
        <p class="description primary-description" v-html="$t('ai-sections.add-variables-text')"></p>
        <!-- Actual mapping list here -->
        <div class="list-wrapper custom-scrollbar">
          <AiMappingList
            :mappings="templateSectionCopy.mappings"
            @add-mapping="addNewMapping"
            @remove-mapping="removeMapping"
          />
        </div>
        <!-- Actual mapping list here -->
      </main>
      <aside class="settings" :class="{ 'collapsed-settings': settingsCollapsed }">
        <header>
          <h4 v-if="!settingsCollapsed" class="text-muted">{{ $t("ai-sections.generation-output") }}</h4>
          <div class="toggle" @click="settingsCollapsed = !settingsCollapsed">
            <span v-show="!settingsCollapsed">
              <i class="fa-light fa-chevron-right"></i>
            </span>
            <span v-show="settingsCollapsed">
              <i class="fa-light fa-chevron-left"></i>
            </span>
          </div>
        </header>

        <div v-if="!settingsCollapsed" class="settings-wrapper">
          <div class="setting-options custom-scrollbar">
            <div class="form-group">
              <label for="textType">{{ $t("ai-sections.text-type") }}</label>
              <select
                v-model="templateSectionCopy.outputSettings.textType"
                name="Text type"
                id="textType"
                class="form-control selector"
              >
                <option value="product">{{ $t("ai-sections.product-desc") }}</option>
                <option value="meta">{{ $t("ai-sections.meta-desc") }}</option>
                <option value="category">{{ $t("ai-sections.category-desc") }}</option>
                <option value="none">{{ $t("ai-sections.no-desc") }}</option>
              </select>
            </div>

            <div class="form-group">
              <label for="tone">{{ $t("ai-sections.tone-of-voice") }}</label>
              <select
                v-model="templateSectionCopy.outputSettings.tone"
                name="tone"
                id="tone"
                class="form-control selector"
              >
                <option value="selling">{{ $t("ai-sections.selling") }}</option>
                <option value="factual">{{ $t("ai-sections.factual") }}</option>
                <option value="funny">{{ $t("ai-sections.funny") }}</option>
                <option value="ironic">{{ $t("ai-sections.ironic") }}</option>
                <option value="creative">{{ $t("ai-sections.creative") }}</option>
                <option value="neutral">{{ $t("ai-sections.neutral") }}</option>
              </select>
            </div>

            <div v-if="!showMetaDescriptionOptions">
              <div class="form-group">
                <label for="heading">{{ $t("ai-sections.add-heading") }}</label>
                <select
                  v-model="templateSectionCopy.outputSettings.heading"
                  name="heading"
                  id="heading"
                  class="form-control selector"
                >
                  <option value="">{{ $t("ai-sections.no-heading") }}</option>
                  <option value="h1">{{ $t("heading") }} 1</option>
                  <option value="h2">{{ $t("heading") }} 2</option>
                  <option value="h3">{{ $t("heading") }} 3</option>
                  <option value="h4">{{ $t("heading") }} 4</option>
                  <option value="h5">{{ $t("heading") }} 5</option>
                </select>
              </div>

              <div class="form-group">
                <label for="sections">{{ $t("ai-sections.text-sections") }}</label>
                <select
                  v-model="templateSectionCopy.outputSettings.sections"
                  name="sections"
                  id="sections"
                  class="form-control selector"
                >
                  <option value="1">1 {{ $t("section") }}</option>
                  <option value="2">2 {{ $t("sections") }}</option>
                  <option value="3">3 {{ $t("sections") }}</option>
                  <option value="4">4 {{ $t("sections") }}</option>
                  <option value="random">{{ $t("ai-sections.be-creative") }}</option>
                </select>
              </div>

              <div class="form-group">
                <label for="list">{{ $t("ai-sections.add-list") }}</label>
                <select
                  v-model="templateSectionCopy.outputSettings.list"
                  name="list"
                  id="list"
                  class="form-control selector"
                >
                  <option value="">{{ $t("ai-sections.no-list") }}</option>
                  <option value="specs">{{ $t("ai-sections.spec-list") }}</option>
                  <option value="sellingPoints">{{ $t("ai-sections.selling-list") }}</option>
                  <option value="features">{{ $t("ai-sections.feature-list") }}</option>
                  <option value="random">{{ $t("ai-sections.be-creative") }}</option>
                </select>
              </div>
            </div>
            <custom-input
              class="language-selector"
              v-model="templateSectionCopy.outputSettings.language"
              :textColor="'#fff'"
              :activeColor="'#fff'"
              :inputType="'text'"
              :label="'Language'"
              :placeholder="$t('profile.pr-lang-en')"
              :labelBG="'#3F464A'"
              :name="'language'"
            />

            <custom-slider
              class="text-length-slider"
              :min="characterOptions.min"
              :step="characterOptions.step"
              :steps="characterOptions.steps"
              :label="$t('ai-sections.limit-length')"
              :subText="$t('ai-sections.chars')"
              name="Character slider"
              id="charSlider"
              :activeColor="'var(--ai-secondary)'"
              v-model="templateSectionCopy.outputSettings.characters"
              @input="(val) => (templateSectionCopy.outputSettings.characters = val)"
            />

            <div class="ai-enabler ">
              <label>{{ $t("ai-sections.enable-ai") }}</label>
              <div class="ai-wrapper" @click="toggleStatus">
                <p>{{ aiStatusMessage }}</p>

                <div class="toggle-wrapper">
                  <span :class="{ 'ai-active': !templateSectionCopy.active }">
                    <i class="fa-light fa-moon-stars"></i>
                  </span>
                  <toggle-button
                    name="toggle"
                    class="toggle"
                    :onToggleButton="toggleStatus"
                    :value="templateSectionCopy.active"
                    noLabel
                    :activeColor="'var(--ai-secondary)'"
                  />
                  <span :class="{ 'ai-active': templateSectionCopy.active }">
                    <i class="fa-light fa-bolt"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="footer" @click="onSave()">
            <div class="done">{{ $t("ai-sections.done") }} <i class="fa-light fa-check"></i></div>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import AiMappingList from "@/components/AiMappingList";
import ToggleButton from "@/components/ToggleButton";
import EventBus from "@/assets/js/eventBus.js";
import CustomSlider from "@/components/CustomSlider.vue";
import CustomInput from "@/components/CustomInput";
import LeaveEditor from "@/components/modals/LeaveEditor";
import VariableHelpers from "@/assets/js/variableHelpers.js";
export default {
  components: {
    ToggleButton,
    CustomInput,
    AiMappingList,
    CustomSlider
  },
  props: {
    templateSection: Object
  },
  data() {
    return {
      templateSectionCopy: null,
      settingsCollapsed: false,
      changes: false,
      ready: false
    };
  },
  methods: {
    toggleStatus($event) {
      $event.stopPropagation();
      this.templateSectionCopy.active = !this.templateSectionCopy.active;
    },
    onSave() {
      if (this.changes) {
        this.$store.commit("overwriteTemplateSection", this.templateSectionCopy);
        this.$store.state.unsavedChanges = true;
        this.$store.dispatch("getTemplatePreviewForSection", this.templateSectionCopy.id);

        if (this.isDemo == true) {
          this.$store.dispatch("saveTemplateChanges", true);
        }
      }
      this.close();
    },

    onClose() {
      if (!this.changes) this.close();
      else {
        this.$modal.show(
          LeaveEditor,
          {
            reverse: true,
            title: this.$t("close-section.title"),
            description: this.$t("close-section.description"),
            thirdOption: true,
            thirdOptionText: this.$t("close-section.trash"),
            confirmButtonText: this.$t("close-section.keep"),
            onConfirm: () => {
              this.onSave();
            },
            onThirdOptionClick: () => {
              this.close();
            },
            confirmBtnPrimaryColor: true
          },
          { width: 450, height: "auto", adaptive: true }
        );
      }
    },

    close() {
      this.$store.commit("setSentenceBeingEdited", null);
      this.$store.commit("setTemplateSectionBeingEdited", null);
      this.$store.commit("setSentenceEditorPreview", null);

      this.templateSectionCopy = null;
    },

    addNewMapping(val) {
      this.templateSectionCopy.addNewMapping(val);
    },
    removeMapping(index) {
      this.templateSectionCopy.removeMapping(index);
    }
  },
  computed: {
    aiStatusMessage() {
      return this.templateSectionCopy && this.templateSectionCopy.active
        ? this.$t("ai-sections.status-active")
        : this.$t("ai-sections.status-inactive");
    },

    showMetaDescriptionOptions() {
      return this.templateSectionCopy && this.templateSectionCopy.outputSettings.textType == "meta";
    },

    characterOptions() {
      if (this.showMetaDescriptionOptions) {
        return {
          min: 100,
          step: 10,
          steps: 10
        };
      } else {
        return {
          min: 100,
          step: 300,
          steps: 9
        };
      }
    },

    isDemo() {
      return this.$store.state.editorIsDemoVersion;
    }
  },
  mounted() {
    // Clone the template section and make ready for editing
    this.templateSectionCopy = this.templateSection.clone();
    this.$store.commit("setTemplateSectionBeingEdited", this.templateSectionCopy);
    if (this.templateSectionCopy.active === undefined) this.templateSectionCopy.active = false;

    // Activate change-watcher
    this.$nextTick(() => (this.ready = true));
  },
  created() {
    EventBus.$emit("close_panel");
  },
  destroyed() {
    EventBus.$emit("open_panel");
  },
  watch: {
    templateSectionCopy: {
      handler: function() {
        if (this.ready) {
          this.changes = true;
        }
      },
      deep: true
    }
  }
};
</script>

<style lang="less">
#aiPopover {
  position: fixed;
  background-color: rgba(61, 70, 74, 0.4);
  width: calc(100%);
  height: calc(100% - 55px);
  top: 55px;
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
  .ai-close {
    cursor: pointer;
  }
  .main {
    padding-bottom: 18px;
  }
  .generation-title {
    margin-top: 48px;
  }

  .ai-content {
    width: calc(100% - 30px);
    height: calc(100% - 30px);
    background: var(--charcoal);
    border-radius: var(--standard-border-radius);
    color: #fff;

    display: flex;
    flex-direction: row;

    .main {
      flex-grow: 1;
      display: flex;
      flex-direction: column;

      .primary-description {
        color: #fff;
        padding: 12px;
      }

      .list-wrapper {
        flex-grow: 1;
        overflow: auto;
        padding-bottom: 18px;
      }

      .footer {
        display: flex;
        justify-content: center;
        position: sticky;
        bottom: 0;
        align-items: center;
        padding: 8px 12px;
        border-top: 1px solid var(--charcoal-border);
        .done {
          cursor: pointer;
          background-color: var(--charcoal);
          margin: 0;
        }
      }
    }
    .settings {
      background: var(--charcoal-115);
      width: clamp(300px, 25%, 400px);
      display: flex;
      justify-content: space-between;
      flex-direction: column;
      border-radius: 0 10px 10px 0;
      border-left: 1px solid var(--charcoal-border);
      &.collapsed-settings {
        width: 40px;
      }
      .footer {
        display: flex;
        cursor: pointer;
        justify-content: center;
        bottom: 0;
        z-index: 1;
        align-items: center;
        padding: 8px 12px;
        border-top: 1px solid var(--charcoal-border);
        .done {
          cursor: pointer;
          margin: 0;
        }
      }

      .settings-wrapper {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        flex-grow: 1;
        overflow: auto;
      }

      .setting-options {
        padding: 12px 20px;
        height: inherit;
        max-height: 100%;
        display: flex;
        flex-direction: column;
        overflow: auto;
      }

      .toggle-wrapper {
        display: flex;
        gap: 8px;
        .ai-active {
          color: var(--ai-secondary);
        }
      }
    }
  }

  .main > header,
  .settings > header {
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid var(--charcoal-border);
    min-height: 40px;
    align-items: center;
    padding: 0 12px;
    h3,
    h4 {
      margin: 0 0 0 12px;
    }
  }
  .main > header {
    justify-content: space-between;
    .left-side-wrapper {
      display: flex;
    }
  }

  .text-length-slider {
    // margin: 12px 0 0px 0 ;
    margin-top: 24px;
  }

  .form-control {
    color: #fff;
  }
}
.input-element .form-control .selector {
  input {
    --text-color: #fff;
  }
}
/*.language-selector{

}*/
.ai-enabler {
  margin-top: 1.4rem;
  label {
    font-size: 12px;
    font-weight: bold;
  }
}
.ai-wrapper {
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;

  & > p {
    font-size: 12px;
    margin-bottom: 0px;
    margin-right: 35px;
  }
}
@media only screen and (max-width: 1272px) {
  #aiPopover {
    .ai-content {
      .settings {
        .setting-options {
          .footer {
            width: calc(299px);
          }
        }
      }
    }
  }
}
</style>
